import { IAnyType } from 'common/types/common';
import { getAlternateLinks, LanguageSwitchData } from 'common/utils/languageSwitch';
import { buildAbsoluteUrl } from 'common/utils/url';
import Head from 'next/head';
import { useTranslation } from 'next-i18next';

interface Props {
  title?: string;
  description?: string;
  image?: string;
  schemaMarkup?: IAnyType | IAnyType[];
  slug: string;
  canonicalSlug?: string;
  robots?: string;
  alternate?: { hrefLang?: string; slug?: string };
  languageSwitch?: LanguageSwitchData;
}

export const Seo = (props: Props) => {
  const { i18n, t } = useTranslation('common');
  const base = t('company.url');
  const baseAlternate = t('company.urlAlternate');
  const title = props.title?.trim() || 'Nordit';
  const description =
    props.description?.trim() ||
    'Nordit designs and builds custom software, mobile apps, web platforms, e-commerce systems, and business tools.';
  const url = buildAbsoluteUrl(base, props.canonicalSlug || props.slug || '/');
  const alternateLinks = getAlternateLinks({
    canonicalHref: url,
    currentLocale: i18n.language,
    alternate: props.alternate,
    baseAlternate,
    localizations: props.languageSwitch?.localizations,
    query: props.languageSwitch?.query,
    routePrefix: props.languageSwitch?.routePrefix,
  });

  const normalizedSchemaMarkup = (() => {
    if (!props.schemaMarkup) {
      return null;
    }

    const items = Array.isArray(props.schemaMarkup) ? props.schemaMarkup : [props.schemaMarkup];
    if (!items.length) {
      return null;
    }

    const graph = items.map(item => {
      if (!item || typeof item !== 'object') {
        return item;
      }

      if (item['@context']) {
        return item;
      }

      return {
        ...item,
        '@context': 'https://schema.org',
      };
    });

    if (graph.length === 1) {
      return graph[0];
    }

    return {
      '@context': 'https://schema.org',
      '@graph': graph.map(item => {
        if (item && typeof item === 'object' && item['@context']) {
          const { ['@context']: _context, ...rest } = item;
          return rest;
        }

        return item;
      }),
    };
  })();

  return (
    <Head>
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <title>{title}</title>
      <link rel="canonical" href={url} />
      {alternateLinks.map(link => (
        <link
          key={`${link.hrefLang}-${link.href}`}
          rel="alternate"
          hrefLang={link.hrefLang}
          href={link.href}
        />
      ))}
      <meta name="description" content={description} />
      {!!props.robots && <meta name="robots" content={props.robots} />}

      <link rel="apple-touch-icon" sizes="180x180" href="/favicon/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon/favicon-16x16.png" />
      <link rel="manifest" href="/favicon/site.webmanifest" />
      <link rel="mask-icon" href="/favicon/safari-pinned-tab.svg" color="#F2C301" />
      <meta name="msapplication-TileColor" content="#F2C301" />

      <meta name="author" content="Nordit d.o.o. - info@nordit.co" />
      <meta name="theme-color" content="#F2C301" />

      <meta property="og:site_name" content="Nordit" />
      <meta property="og:description" content={description} />
      <meta
        property="og:image"
        content={
          props.image ||
          'https://firebasestorage.googleapis.com/v0/b/nordit-83a0b.appspot.com/o/meta%2FhomepageMetaImage.jpg?alt=media&token=f96d5c78-3c09-4bac-92c5-57b5b6591d62'
        }
      />
      <meta property="og:title" content={title} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:site" content="@NorditSoftware" />
      <meta
        name="twitter:image"
        content={
          props.image ||
          'https://firebasestorage.googleapis.com/v0/b/nordit-83a0b.appspot.com/o/meta%2FhomepageMetaImage.jpg?alt=media&token=f96d5c78-3c09-4bac-92c5-57b5b6591d62'
        }
      />
      {!!normalizedSchemaMarkup && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(normalizedSchemaMarkup) }}
        />
      )}
    </Head>
  );
};
